import type { AutorizacaoAdmin } from "@caixaflow/shared";
import { api, ErroApi } from "../api/cliente";
import type { Venda } from "../api/tipos";
import { adicionarNaFila, atualizarNaFila, listarFila, removerDaFila, type ErroDaVenda, type VendaNaFila } from "./fila";

type ResultadoLote =
  | { id: string | null; resultado: "CRIADA" | "JA_EXISTIA"; venda: Venda }
  | { id: string | null; resultado: "RECUSADA" | "ERRO"; codigo: string; mensagem: string; detalhes?: unknown };

const enviarLote = (vendas: VendaNaFila["payload"][]) =>
  api<{ resultados: ResultadoLote[] }>("/vendas/lote", "POST", { vendas });

export type ResultadoRegistro =
  | { situacao: "ENVIADA"; venda: Venda }
  | { situacao: "RECUSADA"; erro: ErroDaVenda }
  | { situacao: "GUARDADA" };

/**
 * Registra a venda do balcão: grava na fila do aparelho e tenta enviar na hora.
 * - Enviou: sai da fila.
 * - Servidor recusou (ex.: acabou o estoque): sai da fila e volta para a comanda — a
 *   mercadoria ainda não foi entregue, o operador corrige e registra de novo.
 * - Sem conexão: fica guardada e é enviada sozinha quando a internet voltar.
 */
export async function registrarVendaNoAparelho(venda: Omit<VendaNaFila, "estado" | "tentativas">): Promise<ResultadoRegistro> {
  await adicionarNaFila({ ...venda, estado: "PENDENTE", tentativas: 0 });
  try {
    const { resultados } = await enviarLote([{ ...venda.payload, viaFilaOffline: false }]);
    const r = resultados[0];
    if (r?.resultado === "CRIADA" || r?.resultado === "JA_EXISTIA") {
      await removerDaFila(venda.id);
      return { situacao: "ENVIADA", venda: r.venda };
    }
    if (r?.resultado === "RECUSADA") {
      await removerDaFila(venda.id);
      return { situacao: "RECUSADA", erro: { codigo: r.codigo, mensagem: r.mensagem, detalhes: r.detalhes } };
    }
  } catch (erro) {
    // Erro de validação da requisição inteira não se resolve sozinho: devolve para a comanda.
    if (erro instanceof ErroApi && erro.status >= 400 && erro.status < 500 && erro.status !== 401) {
      await removerDaFila(venda.id);
      return { situacao: "RECUSADA", erro: { codigo: erro.codigo, mensagem: erro.message } };
    }
  }
  await atualizarNaFila(venda.id, (v) => ({ ...v, tentativas: v.tentativas + 1 }));
  return { situacao: "GUARDADA" };
}

export type ResumoSincronizacao = { enviadas: number; recusadas: number; semConexao: boolean };

let emAndamento: Promise<ResumoSincronizacao> | null = null;

/** Envia as vendas pendentes do usuário (em lotes de até 50). Só uma sincronização por vez. */
export function sincronizarFila(usuarioId: string): Promise<ResumoSincronizacao> {
  emAndamento ??= executar(usuarioId).finally(() => {
    emAndamento = null;
  });
  return emAndamento;
}

/** Vendas novas ainda na primeira tentativa ficam de fora por 1 minuto para não competir com ela. */
const PRIMEIRA_TENTATIVA_MS = 60_000;

async function executar(usuarioId: string): Promise<ResumoSincronizacao> {
  const resumo: ResumoSincronizacao = { enviadas: 0, recusadas: 0, semConexao: false };
  const agora = Date.now();
  const pendentes = (await listarFila(usuarioId)).filter(
    (v) => v.estado === "PENDENTE" && (v.tentativas > 0 || agora - Date.parse(v.criadaLocalEm) > PRIMEIRA_TENTATIVA_MS),
  );

  for (let i = 0; i < pendentes.length; i += 50) {
    const lote = pendentes.slice(i, i + 50);
    let resultados: ResultadoLote[];
    try {
      ({ resultados } = await enviarLote(lote.map((v) => ({ ...v.payload, viaFilaOffline: true }))));
    } catch {
      resumo.semConexao = true;
      for (const v of lote) await atualizarNaFila(v.id, (x) => ({ ...x, tentativas: x.tentativas + 1 }));
      return resumo;
    }
    for (const r of resultados) {
      if (!r.id) continue;
      if (r.resultado === "CRIADA" || r.resultado === "JA_EXISTIA") {
        await removerDaFila(r.id);
        resumo.enviadas += 1;
      } else if (r.resultado === "RECUSADA") {
        await atualizarNaFila(r.id, (x) => ({
          ...x,
          estado: "RECUSADA",
          tentativas: x.tentativas + 1,
          erro: { codigo: r.codigo, mensagem: r.mensagem, detalhes: r.detalhes },
        }));
        resumo.recusadas += 1;
      } else {
        await atualizarNaFila(r.id, (x) => ({ ...x, tentativas: x.tentativas + 1 }));
      }
    }
  }
  return resumo;
}

/** Recoloca uma venda recusada na fila (ex.: depois que o ADMIN lançou estoque ou reabriu o caixa). */
export function tentarDeNovo(id: string) {
  return atualizarNaFila(id, (v) => ({ ...v, estado: "PENDENTE", erro: undefined, tentativas: Math.max(1, v.tentativas) }));
}

/**
 * Aceita a venda recusada com a autorização de um ADMIN digitada no aparelho (PIN ou e-mail e
 * senha). Sem `autorizacao`, vale a sessão — quando quem está logado é ADMIN.
 */
export async function autorizarVendaRecusada(venda: VendaNaFila, autorizacao: AutorizacaoAdmin | undefined): Promise<Venda> {
  const registrada = await api<Venda>("/vendas/com-autorizacao", "POST", {
    venda: { ...venda.payload, viaFilaOffline: true },
    autorizacao,
  });
  await removerDaFila(venda.id);
  return registrada;
}
